import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { ConfigProvider, App as AntdApp, Spin } from 'antd';
import esES from 'antd/locale/es_ES';
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router';

import { ProtectedRoute } from '@/core/auth/ProtectedRoute';
import { queryClient } from '@/core/queryClient';
import AppLayout from '@/shared/components/AppLayout';

// ---- Public pages ----
const LoginPage = lazy(() => import('@/features/auth/LoginPage'));
const FirstSetupPage = lazy(() => import('@/features/auth/FirstSetupPage'));
const NotFoundPage = lazy(() => import('@/shared/components/NotFoundPage'));

// ---- Protected pages ----
const DashboardPage = lazy(() => import('@/features/dashboard/DashboardPage'));

// ---- Feature stubs (replaced in FR-200+) ----
const ProductsRoutes = lazy(() => import('@/features/products/routes'));
const CategoriesRoutes = lazy(() => import('@/features/categories/routes'));
const StockRoutes = lazy(() => import('@/features/stock/routes'));
const WarehousesRoutes = lazy(() => import('@/features/warehouses/routes'));
const SuppliersRoutes = lazy(() => import('@/features/suppliers/routes'));
const CustomersRoutes = lazy(() => import('@/features/customers/routes'));
const ReportsRoutes = lazy(() => import('@/features/reports/routes'));
const PlatformRoutes = lazy(() => import('@/features/platform/routes'));
const UsersRoutes = lazy(() => import('@/features/users/routes'));

// ---- Loading fallback ----
const PageSpinner = (
  <div
    style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Spin size="large" />
  </div>
);

const InlineSpinner = (
  <Spin size="large" style={{ display: 'block', margin: '40px auto' }} />
);

// ---- Router ----
const router = createBrowserRouter([
  {
    // Root redirect to dashboard
    path: '/',
    element: <Navigate to="/dashboard" replace />,
  },
  {
    // Public: Login
    path: '/login',
    element: (
      <Suspense fallback={PageSpinner}>
        <LoginPage />
      </Suspense>
    ),
  },
  {
    // Semi-protected: First-setup page.
    // Accessible only when authenticated AND mustChangePassword === true.
    // The page itself guards both conditions internally (redirect to /login or /dashboard).
    path: '/first-setup',
    element: (
      <Suspense fallback={PageSpinner}>
        <FirstSetupPage />
      </Suspense>
    ),
  },
  {
    // Protected layout shell — ProtectedRoute renders Outlet which is AppLayout
    element: <ProtectedRoute />,
    children: [
      {
        element: <AppLayout />,
        children: [
          {
            path: '/dashboard',
            handle: { breadcrumb: 'navigation.dashboard' },
            element: (
              <Suspense fallback={InlineSpinner}>
                <DashboardPage />
              </Suspense>
            ),
          },
          {
            path: '/products/*',
            handle: { breadcrumb: 'navigation.products' },
            element: (
              <Suspense fallback={InlineSpinner}>
                <ProductsRoutes />
              </Suspense>
            ),
          },
          {
            path: '/categories/*',
            handle: { breadcrumb: 'navigation.categories' },
            element: (
              <Suspense fallback={InlineSpinner}>
                <CategoriesRoutes />
              </Suspense>
            ),
          },
          {
            path: '/stock/*',
            handle: { breadcrumb: 'navigation.stock' },
            element: (
              <Suspense fallback={InlineSpinner}>
                <StockRoutes />
              </Suspense>
            ),
          },
          {
            path: '/warehouses/*',
            handle: { breadcrumb: 'navigation.warehouses' },
            element: (
              <Suspense fallback={InlineSpinner}>
                <WarehousesRoutes />
              </Suspense>
            ),
          },
          {
            path: '/suppliers/*',
            handle: { breadcrumb: 'navigation.suppliers' },
            element: (
              <Suspense fallback={InlineSpinner}>
                <SuppliersRoutes />
              </Suspense>
            ),
          },
          {
            path: '/customers/*',
            handle: { breadcrumb: 'navigation.customers' },
            element: (
              <Suspense fallback={InlineSpinner}>
                <CustomersRoutes />
              </Suspense>
            ),
          },
          {
            path: '/reports/*',
            handle: { breadcrumb: 'navigation.reports' },
            element: (
              <Suspense fallback={InlineSpinner}>
                <ReportsRoutes />
              </Suspense>
            ),
          },
          {
            // Administracion de tenants: solo PymeCode. Sin este guard, cualquier
            // usuario autenticado llegaba tipeando la URL (la API responde 403,
            // pero la pantalla no deberia ser alcanzable).
            path: '/platform/applications',
            handle: { breadcrumb: 'navigation.administration' },
            element: <ProtectedRoute requirePlatformAdmin />,
            children: [
              {
                index: true,
                element: (
                  <Suspense fallback={InlineSpinner}>
                    <PlatformRoutes />
                  </Suspense>
                ),
              },
            ],
          },
          {
            path: '/users',
            handle: { breadcrumb: 'navigation.users' },
            element: (
              <Suspense fallback={InlineSpinner}>
                <UsersRoutes />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
  {
    path: '*',
    element: (
      <Suspense fallback={PageSpinner}>
        <NotFoundPage />
      </Suspense>
    ),
  },
]);

// ---- App Shell ----
function AppShell() {
  return (
    <ConfigProvider locale={esES}>
      <AntdApp>
        <RouterProvider router={router} />
      </AntdApp>
    </ConfigProvider>
  );
}

// ---- Root with Providers ----
export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppShell />
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}
